import { useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  FileText,
  Loader2,
  Search,
  Sparkles,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ResearchStatus, ResearchPhase } from '../../chat/types';

interface Props {
  status: ResearchStatus;
}

/**
 * Live progress panel for a research request — mirrors
 * frontend-prod/components/molecules/ResearchStatusPanel.
 *
 * Rendered by TaskPane.tsx while `chat.researchStatus != null`, replacing
 * the ThinkingAccordion during in-flight research. Once research completes
 * (status cleared) the final answer bubble takes over.
 */
export function ResearchStatusPanel({ status }: Props): JSX.Element {
  const [showSubQueries, setShowSubQueries] = useState(false);
  const PhaseIcon = phaseIcon(status.phase);
  const stateColor = stateColorClass(status.state);

  const counters = status.counters ?? {};
  const hasCounters =
    typeof counters.sub_queries_total === 'number' ||
    typeof counters.sub_queries_done === 'number' ||
    typeof counters.db_citations === 'number' ||
    typeof counters.web_citations === 'number';

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 mb-3">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
            stateColor,
          )}
        >
          {status.state === 'in_progress' || status.state === 'started' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : status.state === 'failed' ? (
            <XCircle className="w-3.5 h-3.5" />
          ) : status.state === 'completed' ? (
            <CheckCircle2 className="w-3.5 h-3.5" />
          ) : (
            <PhaseIcon className="w-3.5 h-3.5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold capitalize">
              {status.phase ?? 'Working'}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {status.state ?? ''}
            </span>
          </div>
          {status.label && (
            <div className="text-xs text-muted-foreground truncate" title={status.label}>
              {status.label}
            </div>
          )}
        </div>
      </div>

      {hasCounters && (
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
          {typeof counters.sub_queries_total === 'number' && (
            <CounterChip
              label="sub-queries"
              value={`${counters.sub_queries_done ?? 0}/${counters.sub_queries_total}`}
            />
          )}
          {typeof counters.db_citations === 'number' && counters.db_citations > 0 && (
            <CounterChip label="DB" value={counters.db_citations} />
          )}
          {typeof counters.web_citations === 'number' && counters.web_citations > 0 && (
            <CounterChip label="web" value={counters.web_citations} />
          )}
        </div>
      )}

      {status.subQueries && status.subQueries.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowSubQueries((x) => !x)}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              className={cn(
                'w-3 h-3 transition-transform',
                showSubQueries && 'rotate-180',
              )}
            />
            {showSubQueries ? 'Hide' : 'Show'} {status.subQueries.length} sub-quer
            {status.subQueries.length === 1 ? 'y' : 'ies'}
          </button>
          {showSubQueries && (
            <ul className="mt-1.5 space-y-1 text-[11px] text-muted-foreground list-decimal pl-5">
              {status.subQueries.map((sq, i) => (
                <li key={i} className="leading-snug">
                  {sq.query}
                  {sq.rationale && (
                    <div className="opacity-75 italic">{sq.rationale}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {status.highConfidenceHits && status.highConfidenceHits.length > 0 && (
        <div className="mt-2 text-[11px] text-muted-foreground">
          <div className="font-medium text-foreground mb-1">High-confidence matches</div>
          <ul className="space-y-0.5 list-disc pl-4">
            {status.highConfidenceHits.slice(0, 5).map((h, i) => (
              <li key={i} className="truncate" title={h.title}>
                {h.title ?? 'Untitled'}
                {h.source ? ` (${h.source})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CounterChip({ label, value }: { label: string; value: number | string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-sm bg-background border border-border px-1.5 py-0.5">
      <span className="font-semibold text-foreground">{value}</span>
      <span className="opacity-70">{label}</span>
    </span>
  );
}

function phaseIcon(phase: ResearchPhase | null): LucideIcon {
  switch (phase) {
    case 'decomposition':
      return Sparkles;
    case 'search':
      return Search;
    case 'verification':
      return CheckCircle2;
    case 'compilation':
      return FileText;
    default:
      return Sparkles;
  }
}

function stateColorClass(state: ResearchStatus['state']): string {
  switch (state) {
    case 'completed':
      return 'bg-green-50 text-green-600';
    case 'failed':
      return 'bg-red-50 text-red-600';
    case 'in_progress':
    case 'started':
      return 'bg-blue-50 text-blue-600';
    default:
      return 'bg-muted text-muted-foreground';
  }
}
